import React, { ReactNode } from 'react';
import { Tooltip, Tag } from '@scale/scaleui';
import apiFetch from 'react-scale/lib/api-fetch';
import { useQuery } from 'react-query';
import _ from 'lodash';
import { ProjectPayTier } from '@scale/shared/defaults';

type ChipPayTierProps = {
  projectId?: string;
};

const LowQualityComponent: React.FC<{}> = () => {
  return (
    <Tag variant="danger" icon="triangle-exclamation">
      Low Quality
    </Tag>
  );
};

const StandardRateComponent: React.FC<{}> = () => {
  return <Tag variant="neutral">Standard rate</Tag>;
};

const SilverTierComponent: React.FC<{}> = () => {
  return (
    <Tag variant="neutral">
      <img src="/static/images/pay-silver-trophy.svg" className="w-4 h-4" />
      <span className="ml-2">Silver quality tier</span>
    </Tag>
  );
};

const GoldTierComponent: React.FC<{}> = () => {
  return (
    <Tag variant="warning">
      <img src="/static/images/pay-gold-trophy.svg" className="w-4 h-4" />
      <span className="ml-2">Gold quality tier</span>
    </Tag>
  );
};

// Unranked and bronze both result in a low quality warning, silver and gold show the actual tier
const ChipPayTierMap: Record<ProjectPayTier, ReactNode> = {
  [ProjectPayTier.Unranked]: <LowQualityComponent />,
  [ProjectPayTier.Bronze]: <LowQualityComponent />,
  [ProjectPayTier.Silver]: <SilverTierComponent />,
  [ProjectPayTier.Gold]: <GoldTierComponent />,
};

/**
 * If the server shows that our CB has a pay tier associated with their current queue assignment, we will display the pay tier in this component.
 */
export const ChipPayTier: React.FC<ChipPayTierProps> = ({ projectId }) => {
  const { data, isLoading } = useQuery(
    'chipPayTier',
    async () =>
      await apiFetch(null, 'internal/scaler/pay_rate_tier', {
        query: { projectId },
      }),
  );

  // Don't show this chip at all if pay tiers isn't set up for this project
  if (isLoading || !data?.payTiersApplicable) {
    return null;
  }

  // If the user has no pay tier due to not enough samples, then display the standard rate
  const component = _.isNil(data?.tier) ? (
    <StandardRateComponent />
  ) : (
    ChipPayTierMap[data?.tier as ProjectPayTier]
  );

  return (
    <Tooltip
      target={<span className="ml-4">{component}</span>}
      text={
        <div className="flex flex-col gap-1 text-center text-sm w-52 font-medium">
          {data?.description}
        </div>
      }
      delayDuration={0}
      asChild={false}
    />
  );
};
